import React from "react";
import { isEmpty, first, last, memoize, takeWhile, drop } from "lodash";
import S from "../../styles/components";
import PanelStack from "../../common/Panel/PanelStack";
import Transcript from "./Transcript";
import Keywords from "./Keywords";
import Timeline from "./Timeline";
import Metrics from "./Metrics";
import Classifiers from "./Classifiers";
import SummaryView from "./SummaryView";
import Controls from "./Controls";
import MiniPlayer from "./MiniPlayer";
import dayjs from "dayjs";

/* eslint react/forbid-prop-types:0 */

function setParsedCallDate(time) {
  return time ? dayjs(time).format("MMM DD, YYYY, hh:mm:ss A") : "";
}

function makeSentimentMapByStartAndEndTime(allSpeakerSentiments) {
  const sentimentMapByStartAndEndTime = {};

  if (!isEmpty(allSpeakerSentiments)) {
    allSpeakerSentiments?.forEach((speakerSentiment) => {
      const { speakerName, sentimentValues } = speakerSentiment;
      sentimentMapByStartAndEndTime[speakerName] = {};
      sentimentValues?.forEach((sentiment) => {
        sentimentMapByStartAndEndTime[speakerName][
          `${sentiment.s}-${sentiment.e}`
        ] = sentiment.v;
      });
    });
  }
  return sentimentMapByStartAndEndTime;
}
class Index extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      marks: [],
      markedRanges: [],
      isMiniPlayerVisible: false,
      playerTopSectionHeight: 0,
    };

    this.markTranscript = this.markTranscript.bind(this);
    this.setSpeakerSentimentValue = this.setSpeakerSentimentValue.bind(this);
  }
  setClickMinMaxPlayerVisible = () => {
    this.setState({
      isMiniPlayerVisible: !this.state.isMiniPlayerVisible,
    });

    const { handleMinimizePlayer } = this.props;

    if (handleMinimizePlayer) {
      handleMinimizePlayer(!this.state.isMiniPlayerVisible);
    }
  };

  setSpeakerSentimentValue = (nextSet, speakerSentimentsMap = {}) => {
    if (!isEmpty(speakerSentimentsMap)) {
      return speakerSentimentsMap[`${first(nextSet).s}-${last(nextSet).e}`];
    }
    return null;
  };

  computeTurns = memoize((words = [], speakerSentiments = []) => {
    const turnsSet = [];
    const isMultiChannel = words.some((word) => word.m === "turn");
    let remainingWords = [...words];
    const sentimentMapByStartAndEndTime =
      makeSentimentMapByStartAndEndTime(speakerSentiments);
    while (remainingWords.length) {
      const nextSet = takeWhile(
        remainingWords,
        (word, i) => i === 0 || word.m !== (isMultiChannel ? "turn" : "punc")
      );
      turnsSet.push({
        start: first(nextSet).s,
        end: last(nextSet).e,
        value:
          this.setSpeakerSentimentValue(
            nextSet,
            sentimentMapByStartAndEndTime[first(nextSet).w]
          ) || 0,
        speaker: isMultiChannel ? first(nextSet).w : null,
      });
      remainingWords = drop(remainingWords, nextSet.length);
    }
    return turnsSet;
  });

  markTranscript(ranges) {
    this.setState({
      markedRanges: ranges,
    });
  }

  // Sets the height of the transcript grid row to fit to bottom of page rather than extend beyond viewport
  updatePlayerTopSectionHeight() {
    // Get the heights for each part of the player that is expected to render
    // in order to get an offset to determine appropriate height
    const controlHeight = document.querySelector(".control")?.offsetHeight || 0;
    const progressHeight =
      document.querySelector(".progress")?.offsetHeight || 0;
    const hrLineHeight =
      document.querySelector(".panel-hr-line")?.offsetHeight || 0;
    const transcriptGridRowMarginHeight =
      document.querySelector(".transcript-grid-row").style.marginTop || 0;
    const panelStackHeaderHeight =
      document.querySelector(".panel-stack-media-tabs")?.offsetHeight +
        parseInt(
          document.querySelector(".panel-stack-media-tabs")?.style.marginTop
        ) +
        parseInt(
          document.querySelector(".panel-stack-media-tabs")?.style.marginBottom
        ) || 0;

    const standardPlayerHeights =
      controlHeight +
      progressHeight +
      hrLineHeight +
      panelStackHeaderHeight +
      parseInt(transcriptGridRowMarginHeight);

    const trackGroup = document.querySelector(".track-group");
    if (trackGroup) {
      // Weird behavior - DOM does not completely render timeline trackGroup so attempt to get
      // the offsetHeight will return 0. Applying a style height will return the expected offsetHeight.
      // Setting height to '1px' will not actually set height to 1px.
      trackGroup.style.height = "1px";
      if (
        this.state.playerTopSectionHeight !==
        trackGroup.offsetHeight + standardPlayerHeights
      ) {
        return this.setState({
          playerTopSectionHeight:
            trackGroup.offsetHeight + standardPlayerHeights,
        });
      }
    } else if (this.state.playerTopSectionHeight !== standardPlayerHeights) {
      return this.setState({
        playerTopSectionHeight: standardPlayerHeights,
      });
    }
  }

  componentDidMount() {
    this.updatePlayerTopSectionHeight();
  }

  render() {
    const {
      parsedFromAnalytics,
      externalHits,
      isPCIFlagEnabled,
      showPCIFlag,
      handlePCIFlagClick,
      flagAction,
      player,
      hidePlayer,
      transcriptLength,
      mediaUrl,
      showToast
    } = this.props;
    const {
      detectors = [],
      transcript,
      classifiers = [],
      metrics = [],
      metricGroups = [],
      groups,
      topics,
      keyWords,
      categories = [],
      mediaId,
      interactionType,
      speakerSentiments,
      metadata,
      verbs,
      entities,
      fullTurns,
      summary,
      llmCategories
    } = parsedFromAnalytics;

    // EA-4493 metrics are applicable for chat
    // const enableMetricsAndTimeline = !(
    //   interactionType === "chat" || interactionType === "text"
    // );

    const isChatTextMedia =
      interactionType === "chat" || interactionType === "text";

    if (metadata.detail) {
      metadata.detail.parsedDate = setParsedCallDate(
        metadata?.detail?.callDetails?.callStartTime
      );
    }
    return (
      <>
        <S.Grid>
          <S.GridRow>
            <S.GridCell>
              <MiniPlayer
                isMiniPlayerVisible={this.state.isMiniPlayerVisible}
                setClickMinMaxPlayerVisible={this.setClickMinMaxPlayerVisible}
                metaData={metadata}
                hidePlayer={hidePlayer}
                mediaId={mediaId}
              ></MiniPlayer>
            </S.GridCell>
          </S.GridRow>
          <S.GridRow>
            <S.GridCell stretch>
              <Controls
                mediaId={mediaId}
                speakerSentiments={speakerSentiments}
                isPCIFlagEnabled={isPCIFlagEnabled}
                showPCIFlag={showPCIFlag}
                handlePCIFlagClick={handlePCIFlagClick}
                flagAction={flagAction}
                isMiniPlayerVisible={this.state.isMiniPlayerVisible}
                setClickMinMaxPlayerVisible={this.setClickMinMaxPlayerVisible}
                metaData={metadata}
                isChatTextMedia={isChatTextMedia}
                mediaUrl={mediaUrl}
                showToast={showToast}
              />
            </S.GridCell>
          </S.GridRow>
          <S.GridRow
            stretch
            style={{
              display: "inline-flex",
              justifyContent: "space-between",
            }}
          >
            {/* Cell contains timeline and transcript  */}
            <S.GridCell
              stretch
              style={{
                flexDirection: "column",
                width: "100%",
              }}
            >
              <S.GridRow style={{ width: "100%", minHeight: "auto" }}>
                <S.GridCell stretch>
                  <Timeline
                    transcriptLength={transcriptLength}
                    metaData={metadata}
                    isChatTextMedia={isChatTextMedia}
                    detectors={detectors}
                    turns={this.computeTurns(transcript, speakerSentiments)}
                    mediaId={mediaId}
                    updatePlayerTopSectionHeight={() =>
                      this.updatePlayerTopSectionHeight()
                    }
                  />
                </S.GridCell>
              </S.GridRow>

              <hr className="panel-hr-line" />

              <S.GridRow
                style={{
                  width: "100%",
                  overflow: "hidden",
                }}
                className="transcript-grid-row"
              >
                <S.GridCell stretch style={{ height: "100%" }}>
                  <PanelStack>
                    <Transcript
                      fullTurns={fullTurns}
                      title="Transcript"
                      transcript={transcript}
                      markedRanges={this.state.markedRanges}
                      isChatTextMedia={isChatTextMedia}
                      metadata={metadata}
                      isPlayer={false}
                    />
                    {metrics.length ? (
                      <Metrics
                        title="Metrics"
                        metrics={metrics}
                        metricGroups={metricGroups}
                        isPlayer={false}
                        isChatTextMedia={isChatTextMedia}
                      />
                    ) : null}
                    {classifiers.length ? (
                      <Classifiers
                        title="Classifiers"
                        classifiers={classifiers}
                        isPlayer={false}
                      />
                    ) : null}

                    {!isEmpty(summary) ? (
                    <SummaryView
                      title="Summary"
                      summaryData={summary}
                      isPlayer={false}
                    />
                   ) : null} 

                  </PanelStack>
                </S.GridCell>

                {/* Cell contains sidebar containing keywords */}
                {!!categories.length ||
                !!entities.length ||
                !!groups.length ||
                !!keyWords.length ||
                !!topics.length ||
                !!verbs.length ||
                (externalHits &&
                  externalHits.hits &&
                  !!externalHits.hits.length) ? (
                  <S.GridCell style={{ width: "25%", minWidth: "200px" }}>
                    <Keywords
                      categories={categories}
                      entities={entities}
                      externalHits={externalHits}
                      groups={groups}
                      keyWords={keyWords}
                      topics={topics}
                      verbs={verbs}
                      markTranscript={this.markTranscript}
                      llmCategories={llmCategories}
                    />
                  </S.GridCell>
                ) : null}
              </S.GridRow>
            </S.GridCell>
          </S.GridRow>
        </S.Grid>
      </>
    );
  }
}

export default Index;
